import { useAuthStore } from "@/stores/auth";
import { apexDomain } from "@/lib/hostKind";
import { Button } from "@/components/ui/Button";

/**
 * Shown when a signed-in user lands on a club subdomain they're not a
 * member of (multi-club). The shared .tktic.com session cookie is valid
 * across subdomains, so the user IS authenticated — they just have no
 * membership in THIS club. The backend already 403/404s their data; this
 * renders that rejection gracefully and offers a switch to a club they
 * belong to.
 *
 * Switching clubs is a FULL navigation (`window.location.assign`) to the
 * other subdomain — a fresh document resets the query cache and the
 * Kratos origin cleanly, which an SPA route could not.
 */
export function NotAMemberOfClubScreen() {
  const currentClub = useAuthStore((s) => s.currentClub);
  const clubMemberships = useAuthStore((s) => s.clubMemberships);

  // Build URLs from the build's apex (tktic.com in prod, dev.tktic.com on the
  // dev channel) so the switcher lands on the right host per environment.
  const apex = apexDomain();
  const goToClub = (subdomain: string) => {
    window.location.assign(`https://${subdomain}.${apex}`);
  };

  return (
    <main
      data-testid="not-a-member-of-club"
      className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-4 px-4 text-center"
    >
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-lg text-ink">
          You’re not a member of {currentClub?.name ?? "this club"}
        </h1>
        <p className="text-sm text-ink-muted">
          Your account isn’t part of this club. If you belong to another club,
          jump to it below.
        </p>
      </div>

      {clubMemberships.length > 0 ? (
        <ul className="flex w-full flex-col gap-1" data-testid="club-switcher">
          {clubMemberships.map((m) => (
            <li key={m.club_id}>
              <button
                type="button"
                onClick={() => goToClub(m.club_subdomain)}
                data-testid="club-switcher-option"
                data-club-subdomain={m.club_subdomain}
                className="flex w-full items-center justify-between rounded-inset border border-rule/60 bg-elevated px-3 py-2 text-left text-sm text-ink hover:bg-field"
              >
                <span className="truncate">{m.club_name}</span>
                <span className="font-mono text-[0.6rem] text-ink-muted">
                  {m.club_subdomain}.{apex}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-muted">
          You don’t belong to any club yet. Ask a club admin to invite you.
        </p>
      )}

      <Button variant="outline" size="sm" onClick={() => goToClub("www")}>
        Back to {apex}
      </Button>
    </main>
  );
}
